import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, MessageCircle, Plus } from "lucide-react";
import { Reveal, PageHeader } from "@/components/Reveal";
import { EnquiryForm } from "@/components/EnquiryForm";
import { FAQS } from "@/lib/content";
import { useContent } from "@/context/ContentContext";

export default function Contact() {
  const { content } = useContent();
  const s = content?.settings || {};
  const [open, setOpen] = useState(0);

  const cards = [
    {
      icon: Phone,
      label: "Call Us",
      value: s.phone || "+971 4 276 9797",
      href: `tel:${(s.phone || "").replace(/[^+\d]/g, "")}`,
      testid: "contact-call-card",
    },
    {
      icon: MessageCircle,
      label: "WhatsApp",
      value: "Chat instantly with our concierge",
      href: s.whatsapp ? `https://wa.me/${s.whatsapp}` : null,
      testid: "contact-whatsapp-card",
    },
    {
      icon: Mail,
      label: "Email",
      value: s.email || "info@royalskin.clinic",
      href: `mailto:${s.email || "info@royalskin.clinic"}`,
      testid: "contact-email-card",
    },
    { icon: MapPin, label: "Visit Us", value: s.address || "Dubai Healthcare City, Dubai, UAE", href: null, testid: "contact-address-card" },
    { icon: Clock, label: "Opening Hours", value: s.hours || "Mon – Sat: 12:00 PM – 8:00 PM", href: null, testid: "contact-hours-card" },
  ];

  return (
    <div className="px-4 pb-24 pt-44 sm:px-8 lg:px-16" data-testid="contact-page">
      <PageHeader
        eyebrow="Contact & Booking"
        title="We Would Love to Meet You"
        subtitle="Request a consultation below, or reach us instantly by phone, WhatsApp or email."
        testid="contact-title"
      />

      <div className="mx-auto mt-14 grid max-w-6xl gap-10 lg:grid-cols-5">
        <Reveal className="lg:col-span-3">
          <div className="glass rounded-[2.5rem] p-8 sm:p-10" data-testid="contact-form-card">
            <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Request a Consultation</h2>
            <p className="mt-2 text-sm font-light text-[#5A504B]">
              Our concierge team responds within 24 hours — usually much sooner.
            </p>
            <div className="mt-7">
              <EnquiryForm prefix="contact-enquiry" />
            </div>
          </div>
        </Reveal>

        <div className="space-y-4 lg:col-span-2">
          {cards.map((c, i) => {
            const inner = (
              <div className="glass glass-card-hover flex items-start gap-4 rounded-3xl p-5" data-testid={c.testid}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#D4AF37]/15">
                  <c.icon className="h-5 w-5 text-[#B38B2D]" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-[10px] font-medium uppercase tracking-[0.2em] text-[#A39288]">{c.label}</span>
                  <span className="mt-1 block text-sm font-medium text-[#1A1615]">{c.value}</span>
                </span>
              </div>
            );
            return (
              <Reveal key={c.label} delay={i * 0.08}>
                {c.href ? (
                  <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="block">
                    {inner}
                  </a>
                ) : (
                  inner
                )}
              </Reveal>
            );
          })}
        </div>
      </div>

      {/* FAQ */}
      <section className="mx-auto mt-24 max-w-4xl" data-testid="faq-section">
        <div className="text-center">
          <Reveal><p className="eyebrow">Questions, Answered</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-3 font-serif text-3xl font-medium text-[#1A1615] sm:text-4xl">Before You Ask</h2>
          </Reveal>
        </div>
        <div className="mt-10 space-y-4">
          {FAQS.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.06}>
              <div className="glass overflow-hidden rounded-3xl">
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  aria-expanded={open === i}
                  className="flex w-full items-center justify-between gap-6 px-7 py-5 text-left"
                >
                  <span className="font-serif text-lg font-semibold text-[#1A1615] sm:text-xl">{f.q}</span>
                  <motion.span
                    animate={{ rotate: open === i ? 45 : 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/40 text-[#B38B2D]"
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="max-w-2xl px-7 pb-6 text-sm font-light leading-relaxed text-[#5A504B]">{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {s.map_embed_url && (s.section_visibility?.contact_map ?? true) && (
        <Reveal className="mx-auto mt-14 max-w-6xl">
          <div className="glass overflow-hidden rounded-[2.5rem] p-2" data-testid="contact-map-card">
            <iframe
              src={s.map_embed_url}
              title="Royal Skin Cosmetic Clinic location map"
              loading="lazy"
              data-testid="contact-map"
              className="h-80 w-full rounded-[2rem] border-0"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      )}
    </div>
  );
}
